import { computed, type ComputedRef, type Ref, ref, type UnwrapRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { getTranslationNamespaceByRoute } from '@/utils/translation-helper';

// statt einer nur-zu-Typzwecken deklarierten Konstante:
type PageTranslationKeys = 'title' | 'subtitle' | 'headline' | 'opener' | 'back2home';

export type PageTranslations = {
  [key in PageTranslationKeys]?: ComputedRef<string>;
};

export interface OtherTranslations {
  other?: ComputedRef<string>;
  extraInfo?: ComputedRef<string>;
}

type TranslationReturnType<T extends string> = T extends 'pages'
  ? PageTranslations
  : T extends 'other'
    ? OtherTranslations
    : Record<string, any>;

export function useTranslationByRoute<T extends string>({
  subbranch,
  routePath,
}: {
  subbranch?: T;
  routePath?: Ref<string> | string;
}) {
  const route = useRoute();
  const i18n = useI18n(); // <- NICHT destrukturieren
  const translations = ref<TranslationReturnType<T>>({} as TranslationReturnType<T>);

  const currentRoutePath = computed(() => {
    return typeof routePath === 'object' && routePath !== null
      ? routePath.value
      : routePath || route.path;
  });

  const updateTranslations = () => {
    const messages = i18n.getLocaleMessage(i18n.locale.value);
    const fbLocaleRaw = i18n.fallbackLocale.value as unknown;
    const fbLocale = Array.isArray(fbLocaleRaw)
      ? fbLocaleRaw[0]
      : ((fbLocaleRaw as string | undefined) ?? i18n.locale.value);
    const fallbackMessages = i18n.getLocaleMessage(fbLocale);

    // Route-Path normalisieren (z. B. batch-classify -> batchClassify)
    let processedRoutePath = currentRoutePath.value;
    const parts = processedRoutePath.split('/');
    const last = parts[parts.length - 1] ?? '';
    const normalizedLast = last.replace(/-([a-zA-Z])/g, (_, c: string) => c.toUpperCase());
    parts[parts.length - 1] = normalizedLast;
    processedRoutePath = parts.join('/');

    const newTranslations = getTranslationNamespaceByRoute({
      routePath: processedRoutePath,
      messages,
      fallbackMessages,
      subbranch,
    }) as TranslationReturnType<T>;

    translations.value = newTranslations as UnwrapRef<TranslationReturnType<T>>;
  };

  // Watch for changes in locale, route and routePath
  watch([i18n.locale, currentRoutePath], updateTranslations, { immediate: true });

  return computed(() => translations.value);
}

export function useFallbackTranslation(
  primaryKey: string,
  fallbackKey: string,
): ComputedRef<string> {
  const i18n = useI18n();
  return computed(() => {
    return i18n.te(primaryKey) ? i18n.t(primaryKey) : i18n.t(fallbackKey);
  });
}
